import { CheckCircle, Loader2, Search, X } from 'lucide-react';
import { useState } from 'react';
import { trpc } from '~/api/trpc';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';

interface TestAnamneseDialogProps {
    open: boolean;
    onClose: () => void;
}

const TestAnamneseDialog = ({ open, onClose }: TestAnamneseDialogProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
    const [pregnancy, setPregnancy] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const searchResults = trpc.customers.search.useQuery(
        { query: searchQuery, limit: 10 },
        { enabled: searchQuery.length > 1 }
    );

    const selectedCustomer = trpc.customers.getOne.useQuery(
        { id: selectedCustomerId! },
        { enabled: !!selectedCustomerId }
    );

    const createMockMutation = trpc.testing.createMockAnamnese.useMutation({
        onSuccess: (data) => {
            setSuccessMessage(data.message);
        },
    });

    const handleClose = () => {
        setSearchQuery('');
        setSelectedCustomerId(null);
        setPregnancy(false);
        setSuccessMessage(null);
        onClose();
    };

    const handleCreateMockAnamnese = () => {
        if (!selectedCustomerId) {
            return;
        }

        createMockMutation.mutate({
            customerId: selectedCustomerId,
            pregnancy,
        });
    };

    const handleSelectCustomer = (customerId: number) => {
        setSelectedCustomerId(customerId);
        setSearchQuery('');
        setSuccessMessage(null);
    };

    const handleClearSelection = () => {
        setSelectedCustomerId(null);
        setSuccessMessage(null);
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Test Anamnese</DialogTitle>
                    <DialogDescription>
                        Create a mock anamnese (questionnaire response) for a customer to test the GHL sync webhook.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Customer Search */}
                    <div className="space-y-2">
                        <Label>Customer</Label>
                        {selectedCustomerId && selectedCustomer.data?.data ? (
                            <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                                <div>
                                    <p className="font-medium">
                                        {selectedCustomer.data.data.firstName} {selectedCustomer.data.data.lastName}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedCustomer.data.data.email}
                                    </p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleClearSelection}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search customer by name or email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>
                                {searchQuery.length > 1 && (
                                    <div className="border rounded-md max-h-48 overflow-y-auto">
                                        {searchResults.isLoading ? (
                                            <div className="p-4 flex justify-center">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            </div>
                                        ) : searchResults.data?.data && searchResults.data.data.length > 0 ? (
                                            <div className="divide-y">
                                                {searchResults.data.data.map((customer) => (
                                                    <button
                                                        key={customer.id}
                                                        className="w-full p-3 text-left hover:bg-muted/50 transition-colors"
                                                        onClick={() => handleSelectCustomer(customer.id)}
                                                    >
                                                        <p className="font-medium">
                                                            {customer.firstName} {customer.lastName}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {customer.email}
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="p-4 text-sm text-muted-foreground text-center">
                                                No customers found
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pregnancy Toggle */}
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="pregnancy"
                            checked={pregnancy}
                            onCheckedChange={(checked) => setPregnancy(checked === true)}
                        />
                        <Label htmlFor="pregnancy" className="cursor-pointer">
                            Set pregnancy status to <Badge variant={pregnancy ? "default" : "secondary"}>{pregnancy ? "true" : "false"}</Badge>
                        </Label>
                    </div>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-green-800">
                            <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium">Success!</p>
                                <p className="text-sm">{successMessage}</p>
                                <p className="text-sm mt-1">The syncCustomerToGhl webhook has been triggered.</p>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {createMockMutation.isError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-800">
                            <p className="font-medium">Error</p>
                            <p className="text-sm">{createMockMutation.error.message}</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        onClick={handleCreateMockAnamnese}
                        disabled={!selectedCustomerId || createMockMutation.isPending}
                    >
                        {createMockMutation.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Create Mock Anamnese
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TestAnamneseDialog;
